import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import {
  RecurringDocument, RecurringDocumentKind, RecurringDocumentLine, RecurringDocumentPrefill, RecurringDocumentRequest, RecurringFrequency,
} from '../../interfaces/recurringDocument.interface';
import { RecurringDocumentService } from '../../services/recurringDocument.service';
import { CustomerService } from '../../services/customer.service';
import { SupplierService } from '../../services/supplier.service';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { Account, Segment } from '../../interfaces/accounting.interface';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTable from '../common/ScrollableTable';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import TaxCodeSelect from '../tax/TaxCodeSelect';
import SalesCatalogueSelect from '../sales/SalesCatalogueSelect';
import { hasTaxCodes, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { discountError, discountPatch, previewLine, shownDiscountAmount } from '../../utils/salesLineDiscount';
import { leafAccounts } from '../../utils/postingAccounts';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { KIND_LABEL, MODES, nextDates } from './recurringDocumentText';

interface LineRow extends Omit<RecurringDocumentLine, 'quantity' | 'unitPrice'> {
  key: string;
  quantity: number;
  unitPrice: number;
  itemLabel?: string;
}

interface Props {
  open: boolean;
  kind: RecurringDocumentKind;
  /** edit an existing template */
  template?: RecurringDocument | null;
  /** "Make recurring" pre-fill; nothing is saved until the user saves */
  prefill?: RecurringDocumentPrefill | null;
  onClose: () => void;
  onSaved: (t: RecurringDocument) => void;
}

const FREQS: { value: RecurringFrequency; label: string }[] = [
  { value: 'WEEKLY', label: 'Weekly' }, { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' }, { value: 'YEARLY', label: 'Yearly' },
];

const RecurringDocumentForm: React.FC<Props> = ({ open, kind, template, prefill, onClose, onSaved }) => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() ?? '';
  const isBill = kind === 'SUPPLIER_BILL';
  const keyCounter = useRef(0);
  const nextKey = () => String(++keyCounter.current);
  const taxLookup = useTaxCodeLookup(institutionId);
  const showTaxCodes = hasTaxCodes(taxLookup);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);

  const [name, setName] = useState('');
  const [partyId, setPartyId] = useState('');
  const [frequency, setFrequency] = useState<RecurringFrequency>('MONTHLY');
  const [intervalCount, setIntervalCount] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [lastDay, setLastDay] = useState(false);
  const [startDate, setStartDate] = useState(toLocalIsoDate());
  const [endDate, setEndDate] = useState('');
  const [maxOccurrences, setMaxOccurrences] = useState('');
  const [mode, setMode] = useState(MODES[kind][0].value);
  const [dueDays, setDueDays] = useState('30');
  const [validityDays, setValidityDays] = useState('30');
  const [supplierRef, setSupplierRef] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const toRow = (l: RecurringDocumentLine): LineRow => ({
    ...l, key: nextKey(), quantity: Number(l.quantity), unitPrice: Number(l.unitPrice),
    itemLabel: l.itemId ? 'Catalogue item' : undefined,
  });
  const blank = (): LineRow => ({ key: nextKey(), description: '', quantity: 1, unitPrice: NaN });

  // reference data
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      isBill ? SupplierService.listByInstitution(0, 200) : CustomerService.listByInstitution(institutionId, 0, 200),
      AccountService.getAllAccounts(),
      SegmentService.getByInstitution(0, 200).catch(() => ({ content: [] as Segment[] })),
    ]).then(([parties, accs, segPage]) => {
      const content = (parties as { content: (Customer & Supplier)[] }).content ?? [];
      if (isBill) setSuppliers(content); else setCustomers(content);
      setAccounts(Array.isArray(accs) ? accs : []);
      setSegments(((segPage as { content?: Segment[] }).content ?? []).filter(s => s.active !== false));
    }).catch(e => toast.showApiError(e, 'Failed to load form data')).finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // initial values
  useEffect(() => {
    if (!open) return;
    if (template) {
      setName(template.name);
      setPartyId((isBill ? template.supplierId : template.customerId) ?? '');
      setFrequency(template.frequency);
      setIntervalCount(String(template.intervalCount));
      setDayOfMonth(String(template.dayOfMonth ?? 1));
      setLastDay(template.lastDayOfMonth);
      setStartDate(template.startDate);
      setEndDate(template.endDate ?? '');
      setMaxOccurrences(template.maxOccurrences ? String(template.maxOccurrences) : '');
      setMode(template.mode);
      setDueDays(String(template.dueDays ?? 30));
      setValidityDays(String(template.validityDays ?? 30));
      setSupplierRef(template.supplierRefPattern ?? '');
      setNotes(template.notes ?? '');
      setLines(template.lines.map(toRow));
    } else {
      setName(prefill?.name ?? '');
      setPartyId((isBill ? prefill?.supplierId : prefill?.customerId) ?? '');
      setMode(MODES[kind][0].value);
      setStartDate(toLocalIsoDate());
      setDueDays(String(prefill?.dueDays ?? 30));
      setValidityDays(String(prefill?.validityDays ?? 30));
      setSupplierRef(prefill?.supplierRefPattern ?? '');
      setNotes(prefill?.notes ?? '');
      setLines(prefill?.lines?.length ? prefill.lines.map(toRow) : [blank()]);
    }
  }, [open, template, prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLine = (i: number, patch: Partial<LineRow>) => setLines(prev => prev.map((l, x) => (x === i ? { ...l, ...patch } : l)));

  const accountOptions = useMemo(
    () => leafAccounts(accounts.filter(a => a.accountType === (isBill ? 'EXPENSE' : 'REVENUE'))),
    [accounts, isBill],
  );

  const preview = useMemo(
    () => nextDates(frequency, Number(intervalCount) || 1, lastDay ? null : Number(dayOfMonth) || null, lastDay, startDate, 3),
    [frequency, intervalCount, dayOfMonth, lastDay, startDate],
  );
  const net = (l: LineRow) => previewLine(l).net;
  const subtotal = lines.reduce((s, l) => s + net(l), 0);

  const lineInvalid = (l: LineRow) =>
    !(l.quantity > 0) || !(l.unitPrice >= 0) || !l.description.trim() || !!discountError(l) || (isBill && !l.accountId);
  const invalid = !name.trim() || !partyId || lines.length === 0 || lines.some(lineInvalid)
    || (isBill && !(Number(dueDays) >= 0 && dueDays !== ''));

  const save = async () => {
    const req: RecurringDocumentRequest = {
      institutionId, kind, name: name.trim(),
      customerId: isBill ? undefined : partyId, supplierId: isBill ? partyId : undefined,
      frequency, intervalCount: Number(intervalCount) || 1,
      dayOfMonth: frequency === 'WEEKLY' || lastDay ? null : Number(dayOfMonth) || 1,
      lastDayOfMonth: frequency !== 'WEEKLY' && lastDay,
      startDate, endDate: endDate || null, maxOccurrences: maxOccurrences ? Number(maxOccurrences) : null,
      mode, emailOnIssue: false,
      dueDays: kind === 'QUOTATION' ? null : Number(dueDays),
      validityDays: kind === 'QUOTATION' ? Number(validityDays) : null,
      supplierRefPattern: isBill ? (supplierRef.trim() || null) : null,
      notes: notes.trim() || null,
      lines: lines.map(l => ({
        itemId: l.itemId || null, description: l.description.trim(), quantity: l.quantity, unitPrice: l.unitPrice,
        discountPct: l.discountPct ?? null, discountAmount: l.discountPct == null ? (l.discountAmount ?? null) : null,
        taxCodeId: l.taxCodeId || null, accountId: l.accountId || null, segmentId: l.segmentId || null,
      })),
    };
    setSaving(true);
    try {
      const saved = template ? await RecurringDocumentService.update(template.id, req) : await RecurringDocumentService.create(req);
      toast.showSuccess(template ? 'Template saved' : 'Recurring template created');
      onSaved(saved);
    } catch (e) {
      toast.showApiError(e, 'Failed to save the template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog data-help-page="recurringDocs.form.page" open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{template ? 'Edit' : 'New'} recurring {KIND_LABEL[kind].toLowerCase()}</DialogTitle>
      <DialogContent>
        {loading ? <Box sx={{ p: 3 }}><CircularProgress /></Box> : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Nothing is e-mailed to {isBill ? 'suppliers' : 'customers'}. Each occurrence goes through the same checks as a hand-made document
              (inactive party, tax, segment, credit hold, closed period); a failure is listed on the template for retry.
            </Alert>
            {prefill?.warnings?.map(w => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
            <Box data-help-section="recurringDocs.form.section.basics" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <TextField data-help="recurringDocs.form.field.name" label="Template name" value={name} onChange={e => setName(e.target.value)} required
                sx={{ flex: '1 1 240px', maxWidth: 360 }} inputProps={{ maxLength: 255 }} />
              <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                {isBill ? (
                  <SearchableSelect data-help="recurringDocs.form.field.supplier" label="Supplier" options={suppliers} value={partyId} onChange={id => setPartyId(id)} getOptionLabel={s => s.name} />
                ) : (
                  <SearchableSelect data-help="recurringDocs.form.field.customer" label="Customer" options={customers} value={partyId} onChange={id => setPartyId(id)} getOptionLabel={c => c.name} />
                )}
              </Box>
              <TextField data-help="recurringDocs.form.field.mode" select label="Creates" value={mode} onChange={e => setMode(e.target.value as typeof mode)}
                sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                {MODES[kind].map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Box>
            <Box data-help-section="recurringDocs.form.section.schedule" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              <TextField data-help="recurringDocs.form.field.frequency" select label="Frequency" value={frequency} onChange={e => setFrequency(e.target.value as RecurringFrequency)}
                sx={{ flex: '1 1 140px', maxWidth: 200 }}>
                {FREQS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
              </TextField>
              <TextField data-help="recurringDocs.form.field.every" label="Every N" type="number" value={intervalCount} onChange={e => setIntervalCount(e.target.value)}
                inputProps={{ min: 1, max: 52 }} sx={{ flex: '1 1 100px', maxWidth: 140 }} />
              {frequency !== 'WEEKLY' && (
                <>
                  <TextField data-help="recurringDocs.form.field.dayOfMonth" label="Day of month" type="number" value={dayOfMonth} disabled={lastDay}
                    onChange={e => setDayOfMonth(e.target.value)} inputProps={{ min: 1, max: 28 }} sx={{ flex: '1 1 120px', maxWidth: 160 }} />
                  <FormControlLabel data-help="recurringDocs.form.field.lastDay" control={<Checkbox checked={lastDay} onChange={e => setLastDay(e.target.checked)} />} label="Last day" />
                </>
              )}
              <TextField data-help="recurringDocs.form.field.startDate" label="Start date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} required />
              <TextField data-help="recurringDocs.form.field.endDate" label="End date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
              <TextField data-help="recurringDocs.form.field.maxOccurrences" label="Max occurrences" type="number" value={maxOccurrences} onChange={e => setMaxOccurrences(e.target.value)}
                inputProps={{ min: 1, max: 1000 }} sx={{ flex: '1 1 140px', maxWidth: 180 }} />
            </Box>
            <Typography data-help="recurringDocs.form.nextDates" variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="next-dates">
              Next dates: {preview.map(formatDate).join(', ') || '-'}
            </Typography>
            <Box data-help-section="recurringDocs.form.section.terms" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
              {kind === 'QUOTATION' ? (
                <TextField data-help="recurringDocs.form.field.validityDays" label="Valid for (days)" type="number" value={validityDays} onChange={e => setValidityDays(e.target.value)}
                  inputProps={{ min: 1, max: 365 }} sx={{ flex: '1 1 140px', maxWidth: 200 }} />
              ) : (
                <TextField data-help="recurringDocs.form.field.dueDays" label="Due after (days)" type="number" value={dueDays} onChange={e => setDueDays(e.target.value)}
                  inputProps={{ min: 0, max: 365 }} sx={{ flex: '1 1 140px', maxWidth: 200 }} required={isBill} />
              )}
              {isBill && (
                <TextField data-help="recurringDocs.form.field.supplierRef" label="Supplier reference (e.g. RENT-{period})" value={supplierRef} onChange={e => setSupplierRef(e.target.value)}
                  sx={{ flex: '1 1 240px', maxWidth: 360 }} inputProps={{ maxLength: 120 }} />
              )}
              <TextField data-help="recurringDocs.form.field.notes" label="Notes" value={notes} onChange={e => setNotes(e.target.value)}
                sx={{ flex: '1 1 260px', maxWidth: 480 }} inputProps={{ maxLength: 1000 }} />
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
              <Typography variant="h6">Lines</Typography>
              <Button data-help="recurringDocs.form.addLine" variant="outlined" startIcon={<Add />} onClick={() => setLines(prev => [...prev, blank()])}>Add line</Button>
            </Box>
            <ScrollableTable>
              <Table data-help-section="recurringDocs.form.section.lines" size="small">
                <TableHead>
                  <TableRow>
                    {!isBill && <TableCell data-help="recurringDocs.form.col.item">Product / service</TableCell>}
                    <TableCell data-help="recurringDocs.form.col.description">Description</TableCell>
                    <TableCell data-help="recurringDocs.form.col.qty">Qty</TableCell>
                    <TableCell data-help="recurringDocs.form.col.unitPrice">Unit price</TableCell>
                    <TableCell data-help="recurringDocs.form.col.discPct">Disc %</TableCell>
                    <TableCell data-help="recurringDocs.form.col.discAmount">Disc amount</TableCell>
                    {showTaxCodes && <TableCell data-help="recurringDocs.form.col.taxCode">Tax code</TableCell>}
                    <TableCell data-help="recurringDocs.form.col.account">{isBill ? 'Expense account' : 'Revenue account'}</TableCell>
                    <TableCell data-help="recurringDocs.form.col.segment">Segment</TableCell>
                    <TableCell data-help="recurringDocs.form.col.net" align="right">Net</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, i) => (
                    <TableRow key={line.key}>
                      {!isBill && (
                        <TableCell sx={{ minWidth: 190 }}>
                          <SalesCatalogueSelect data-help="recurringDocs.form.line.item" value={line.itemId ?? null} valueLabel={line.itemLabel}
                            onChange={(id, item) => {
                              if (!id || !item) { setLine(i, { itemId: null, itemLabel: undefined }); return; }
                              setLine(i, {
                                itemId: id, itemLabel: `${item.code} - ${item.name}`, description: item.name,
                                unitPrice: item.salePrice != null ? item.salePrice : line.unitPrice,
                                accountId: item.revenueAccountId || line.accountId,
                                taxCodeId: item.taxCodeId || line.taxCodeId,
                              });
                            }} />
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField data-help="recurringDocs.form.line.description" size="small" value={line.description} sx={{ minWidth: 160 }} inputProps={{ 'aria-label': 'Description' }}
                          onChange={e => setLine(i, { description: e.target.value })} />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="recurringDocs.form.line.qty" type="number" size="small" value={Number.isFinite(line.quantity) ? line.quantity : ''} sx={{ width: 80 }}
                          error={!(line.quantity > 0)} inputProps={{ 'aria-label': 'Quantity', step: 'any', min: 0 }}
                          onChange={e => setLine(i, { quantity: e.target.value === '' ? NaN : Number(e.target.value) })} />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="recurringDocs.form.line.unitPrice" type="number" size="small" value={Number.isFinite(line.unitPrice) ? line.unitPrice : ''} sx={{ width: 100 }}
                          error={!(line.unitPrice >= 0)} inputProps={{ 'aria-label': 'Unit price', step: 'any', min: 0 }}
                          onChange={e => setLine(i, { unitPrice: e.target.value === '' ? NaN : Number(e.target.value) })} />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="recurringDocs.form.line.discPct" type="number" size="small" sx={{ width: 80 }} value={line.discountPct ?? ''}
                          error={!!discountError(line)} helperText={discountError(line) ?? undefined}
                          inputProps={{ 'aria-label': 'Disc %', step: 'any', min: 0, max: 100 }}
                          onChange={e => setLine(i, discountPatch('pct', e.target.value))} />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="recurringDocs.form.line.discAmount" type="number" size="small" sx={{ width: 100 }} value={shownDiscountAmount(line)}
                          inputProps={{ 'aria-label': 'Disc amount', step: 'any', min: 0 }}
                          onChange={e => setLine(i, discountPatch('amount', e.target.value))} />
                      </TableCell>
                      {showTaxCodes && (
                        <TableCell>
                          <TaxCodeSelect data-help="recurringDocs.form.line.taxCode" lookup={taxLookup} side={isBill ? 'INPUT' : 'OUTPUT'} value={line.taxCodeId ?? null}
                            onChange={id => setLine(i, { taxCodeId: id })} />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ minWidth: 180 }}>
                          <AccountAutocomplete data-help="recurringDocs.form.line.account" accounts={accountOptions} value={line.accountId ?? ''}
                            label={isBill ? 'Expense account' : 'Revenue account'} placeholder={isBill ? 'Required' : 'Default'}
                            onChange={id => setLine(i, { accountId: id || null })} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SearchableSelect data-help="recurringDocs.form.line.segment" label="Segment" size="small" sx={{ minWidth: 140 }} options={segments} value={line.segmentId ?? ''}
                          onChange={v => setLine(i, { segmentId: v || null })} getOptionLabel={s => `${s.code} - ${s.name}`} clearable />
                      </TableCell>
                      <TableCell align="right">{formatMoney(net(line))}</TableCell>
                      <TableCell>
                        <IconButton data-help="recurringDocs.form.removeLine" size="small" color="error" aria-label="remove line" disabled={lines.length === 1}
                          onClick={() => setLines(prev => prev.filter((_, x) => x !== i))}><Delete /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTable>
            <Typography sx={{ mt: 1, textAlign: 'right' }}>Subtotal (before tax): <strong>{formatMoney(subtotal)}</strong></Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button data-help="recurringDocs.form.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button data-help="recurringDocs.form.save" variant="contained" onClick={save} disabled={saving || loading || invalid}>
          {saving ? <CircularProgress size={20} /> : 'Save template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecurringDocumentForm;
